// src/components/games/crossroads/gameLogic.ts
import { logger } from '../../../utils/logger';
// Core game logic for Crossroads TCG

import {
  GameState,
  PlayerState,
  Phase,
  DeckDefinition,
  Card,
  CardType,
  BattlefieldCreature,
  BattlefieldManaSource,
  ManaPool,
  ElementType,
  CommanderState,
  CombatDeclaration,
  BlockDeclaration,
  hasTypeAdvantage,
  CreatureCard,
  ManaSourceCard,
  ArtCard,
  EnvironmentCard,
  GameEvent,
  ChatMessage,
  OngoingEffect,
  OngoingEffectType,
  OngoingEffectDuration,
  AbilityKeyword,
  ModalChoice,
  ModalChoiceType,
  ModalChoiceOption,
  Trigger,
  TriggerType,
  TriggerContext,
} from './types';
import { ALL_CARDS } from './cardData';
import { ALL_ENVIRONMENT_CARDS } from './environmentDecks';

// ============================================================================
// UTILITY FUNCTIONS
// ============================================================================

// Log a game event for UX feedback
export function logEvent(
  game: GameState,
  type: GameEvent['type'],
  message: string,
  playerId?: number
): void {
  if (!game.eventLog) {
    game.eventLog = [];
  }

  const event: GameEvent = {
    type,
    message,
    timestamp: Date.now(),
    playerId,
  };

  game.eventLog.push(event);

  // Keep only last 20 events
  if (game.eventLog.length > 20) {
    game.eventLog.shift();
  }
}

// Send a chat message
export function sendChatMessage(
  game: GameState,
  playerId: number,
  playerName: string,
  message: string
): void {
  if (!game.chatMessages) {
    game.chatMessages = [];
  }

  const chatMessage: ChatMessage = {
    playerId,
    playerName,
    message,
    timestamp: Date.now(),
  };

  game.chatMessages.push(chatMessage);

  // Keep only last 50 messages
  if (game.chatMessages.length > 50) {
    game.chatMessages.shift();
  }
}

// Generate unique instance ID
let instanceCounter = 0;
export function generateInstanceId(): string {
  return `instance_${Date.now()}_${instanceCounter++}`;
}

// Shuffle array
export function shuffleArray<T>(array: T[]): T[] {
  const shuffled = [...array];
  for (let i = shuffled.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
  }
  return shuffled;
}

// Roll a die
export function rollDie(sides: number): number {
  return Math.floor(Math.random() * sides) + 1;
}

// Flip a coin (true = heads, false = tails)
export function flipCoin(): boolean {
  return Math.random() >= 0.5;
}

// Find a creature on the battlefield by instance ID
export function findCreatureOnBattlefield(game: GameState, creatureId: string): BattlefieldCreature | null {
  for (const player of game.players) {
    const creature = player.battlefield.creatures.find(c => c.instanceId === creatureId);
    if (creature) return creature;
  }
  return null;
}

// Roll a die and track it in game state (for reroll ability)
export function rollDieTracked(
  game: GameState,
  sides: number,
  playerId: number,
  effectType?: 'damage' | 'heal' | 'none',
  targetId?: string,
  targetType?: 'player' | 'creature'
): number {
  const value = Math.floor(Math.random() * sides) + 1;
  game.lastDiceRoll = {
    value,
    playerId,
    rerollable: true,
    effectType,
    targetId,
    targetType,
    amount: value, // Store the rolled amount
  };

  // Log the dice roll
  const playerName = game.players[playerId]?.name || `Player ${playerId + 1}`;
  logEvent(game, 'dice_roll', `${playerName} rolled a ${value} on d${sides}`, playerId);

  return value;
}

// Reroll the last dice roll (for Dice and Autism's reroll ability)
export function rerollLastDie(game: GameState): number | null {
  if (!game.lastDiceRoll || !game.lastDiceRoll.rerollable) {
    return null; // No rerollable die to reroll
  }

  const roll = game.lastDiceRoll;
  const oldValue = roll.value;
  const newValue = Math.floor(Math.random() * 6) + 1; // Assume d6

  // Undo the original effect if tracked
  if (roll.effectType === 'damage' && roll.targetId && roll.targetType && roll.amount) {
    // Heal back the damage
    if (roll.targetType === 'player') {
      healCommander(game, parseInt(roll.targetId.replace('player_', '')), roll.amount);
    } else if (roll.targetType === 'creature') {
      healCreature(game, roll.targetId, roll.amount);
    }
  } else if (roll.effectType === 'heal' && roll.targetId && roll.targetType && roll.amount) {
    // Deal damage to undo healing
    if (roll.targetType === 'player') {
      dealDamage(game, roll.targetId, 'player', roll.amount);
    } else if (roll.targetType === 'creature') {
      dealDamage(game, roll.targetId, 'creature', roll.amount);
    }
  }

  // Redo the effect with new value
  if (roll.effectType === 'damage' && roll.targetId && roll.targetType) {
    dealDamage(game, roll.targetId, roll.targetType, newValue);
  } else if (roll.effectType === 'heal' && roll.targetId && roll.targetType) {
    if (roll.targetType === 'player') {
      healCommander(game, parseInt(roll.targetId.replace('player_', '')), newValue);
    } else if (roll.targetType === 'creature') {
      healCreature(game, roll.targetId, newValue);
    }
  }

  // Update the roll tracking
  game.lastDiceRoll = { ...roll, value: newValue, amount: newValue, rerollable: false };

  // Log the reroll
  const playerName = game.players[roll.playerId]?.name || `Player ${roll.playerId + 1}`;
  logEvent(game, 'dice_roll', `${playerName} rerolled ${oldValue} → ${newValue}`, roll.playerId);

  return newValue;
}

// ============================================================================
// ONGOING EFFECTS SYSTEM
// ============================================================================

// Initialize ongoing effects array if not present
function ensureOngoingEffects(game: GameState): void {
  if (!game.ongoingEffects) {
    game.ongoingEffects = [];
  }
}

// Add an ongoing effect
export function addOngoingEffect(
  game: GameState,
  effect: Omit<OngoingEffect, 'id' | 'createdAtTurn' | 'createdAtPhase'>
): string {
  ensureOngoingEffects(game);

  const effectId = `effect_${Date.now()}_${Math.random()}`;
  const fullEffect: OngoingEffect = {
    ...effect,
    id: effectId,
    createdAtTurn: game.currentPlayerIndex,
    createdAtPhase: game.currentPhase,
  };

  game.ongoingEffects!.push(fullEffect);
  return effectId;
}

// Remove an ongoing effect by ID
export function removeOngoingEffect(game: GameState, effectId: string): void {
  ensureOngoingEffects(game);
  game.ongoingEffects = game.ongoingEffects!.filter(e => e.id !== effectId);
}

// Swap control of a creature temporarily
export function swapCreatureControl(
  game: GameState,
  creatureId: string,
  newControllerId: number,
  duration: OngoingEffectDuration
): string | null {
  const creature = findCreatureOnBattlefield(game, creatureId);
  if (!creature) return null;

  return addOngoingEffect(game, {
    type: OngoingEffectType.CONTROL_SWAP,
    duration,
    targetId: creatureId,
    targetType: 'creature',
    createdByPlayerId: newControllerId,
    originalOwnerId: creature.ownerId,
    newControllerId: newControllerId,
  });
}

// Get all ongoing effects affecting a target
export function getOngoingEffectsForTarget(
  game: GameState,
  targetId: string,
  targetType: 'creature' | 'player'
): OngoingEffect[] {
  ensureOngoingEffects(game);

  return game.ongoingEffects!.filter(effect => {
    // Direct match
    if (effect.targetId === targetId && effect.targetType === targetType) {
      return true;
    }
    // All creatures match
    if (targetType === 'creature' && effect.targetType === 'all_creatures') {
      return true;
    }
    // All players match
    if (targetType === 'player' && effect.targetType === 'all_players') {
      return true;
    }
    // Global effects match everything
    if (effect.targetType === 'global') {
      return true;
    }
    return false;
  });
}

// Check if a creature can attack (not affected by CANT_ATTACK effect)
export function canCreatureAttack(game: GameState, creatureId: string): boolean {
  const effects = getOngoingEffectsForTarget(game, creatureId, 'creature');
  return !effects.some(e => e.type === OngoingEffectType.CANT_ATTACK);
}

// Check if a creature can block (not affected by CANT_BLOCK effect)
export function canCreatureBlock(game: GameState, creatureId: string): boolean {
  const effects = getOngoingEffectsForTarget(game, creatureId, 'creature');
  return !effects.some(e => e.type === OngoingEffectType.CANT_BLOCK);
}

// Check if a creature/mana source can tap (not affected by CANT_TAP effect)
export function canTap(game: GameState, targetId: string): boolean {
  const effects = getOngoingEffectsForTarget(game, targetId, 'creature');
  return !effects.some(e => e.type === OngoingEffectType.CANT_TAP);
}

// Check if a creature/mana source can untap (not affected by CANT_UNTAP effect)
export function canUntap(game: GameState, targetId: string): boolean {
  const effects = getOngoingEffectsForTarget(game, targetId, 'creature');
  return !effects.some(e => e.type === OngoingEffectType.CANT_UNTAP);
}

// Tap a creature
export function tapCreature(game: GameState, creatureId: string): boolean {
  const creature = findCreatureOnBattlefield(game, creatureId);
  if (!creature || creature.isTapped) return false;

  if (!canTap(game, creatureId)) return false;

  creature.isTapped = true;
  logEvent(game, 'ability_activated', `${ALL_CARDS[creature.cardId]?.name || 'Creature'} tapped`);
  return true;
}

// Untap a creature
export function untapCreature(game: GameState, creatureId: string): boolean {
  const creature = findCreatureOnBattlefield(game, creatureId);
  if (!creature || !creature.isTapped) return false;

  if (!canUntap(game, creatureId)) return false;

  creature.isTapped = false;
  return true;
}

// Untap all creatures for a player
export function untapAllCreatures(game: GameState, playerId: number): void {
  const player = game.players[playerId];

  // Untap all creatures
  for (const creature of player.battlefield.creatures) {
    if (creature.isTapped && canUntap(game, creature.instanceId)) {
      creature.isTapped = false;
    }
  }

  // Untap all mana sources
  for (const manaSource of player.battlefield.manaSources) {
    if (manaSource.isTapped && canUntap(game, manaSource.instanceId)) {
      manaSource.isTapped = false;
    }
  }

  // Untap commander
  if (player.commander.tapped) {
    player.commander.tapped = false;
  }
}

// Tap a commander
export function tapCommander(game: GameState, playerId: number): boolean {
  const player = game.players[playerId];
  if (player.commander.tapped) return false;

  player.commander.tapped = true;
  const commanderCard = game.cardInstances[player.commander.cardId];
  if (commanderCard) {
    logEvent(game, 'ability_activated', `${commanderCard.name} tapped`);
  }
  return true;
}

// Untap a commander
export function untapCommander(game: GameState, playerId: number): boolean {
  const player = game.players[playerId];
  if (!player.commander.tapped) return false;

  player.commander.tapped = false;
  return true;
}

// Copy another creature's stats and abilities (for Changeling Mimic)
export function copyCreatureStats(game: GameState, targetCreatureId: string, sourceCreatureId: string): boolean {
  const targetCreature = findCreatureOnBattlefield(game, targetCreatureId);
  const sourceCreature = findCreatureOnBattlefield(game, sourceCreatureId);

  if (!targetCreature || !sourceCreature) return false;

  // Copy stats
  targetCreature.power = sourceCreature.power;
  targetCreature.health = sourceCreature.health;
  targetCreature.maxHealth = sourceCreature.maxHealth;
  targetCreature.currentHealth = sourceCreature.currentHealth;

  // Copy keywords
  targetCreature.keywords = [...sourceCreature.keywords];

  // Copy abilities from the source card
  const sourceCard = ALL_CARDS[sourceCreature.cardId];
  if (sourceCard && sourceCard.type === CardType.CREATURE && sourceCard.abilities) {
    targetCreature.copiedAbilities = [...sourceCard.abilities];
  }

  logEvent(game, 'ability_activated', `${ALL_CARDS[targetCreature.cardId]?.name || 'Creature'} copied ${ALL_CARDS[sourceCreature.cardId]?.name || 'creature'}`);
  return true;
}

// Transform a creature into an exact copy of another (for Fae Shapeshifter)
export function transformCreatureIntoCopy(game: GameState, shapeshifterId: string, targetCreatureId: string): boolean {
  const shapeshifter = findCreatureOnBattlefield(game, shapeshifterId);
  const targetCreature = findCreatureOnBattlefield(game, targetCreatureId);

  if (!shapeshifter || !targetCreature) return false;

  const player = game.players[shapeshifter.ownerId];

  // Remove shapeshifter from battlefield
  const creatureIndex = player.battlefield.creatures.findIndex(c => c.instanceId === shapeshifterId);
  if (creatureIndex === -1) return false;

  player.battlefield.creatures.splice(creatureIndex, 1);

  // Add shapeshifter to discard
  player.discard.push(shapeshifter.cardId);

  // Create exact copy of target creature
  const copy: BattlefieldCreature = {
    instanceId: generateInstanceId(),
    cardId: targetCreature.cardId,
    ownerId: shapeshifter.ownerId, // Owned by shapeshifter's controller
    power: targetCreature.power,
    health: targetCreature.health,
    maxHealth: targetCreature.maxHealth,
    currentHealth: targetCreature.currentHealth,
    isTapped: false,
    summoningSick: false, // Copy enters untapped and without summoning sickness
    keywords: [...targetCreature.keywords],
    counters: { plusOnePlusOne: 0, minusOneMinusOne: 0 },
    activeAbilities: [],
  };

  // Add copy to battlefield
  player.battlefield.creatures.push(copy);

  logEvent(game, 'ability_activated', `Fae Shapeshifter transformed into ${ALL_CARDS[targetCreature.cardId]?.name || 'creature'}`);
  return true;
}

// Check if a creature MUST attack (affected by MUST_ATTACK effect)
export function mustCreatureAttack(game: GameState, creatureId: string): boolean {
  const effects = getOngoingEffectsForTarget(game, creatureId, 'creature');
  return effects.some(e => e.type === OngoingEffectType.MUST_ATTACK);
}

// Check if abilities can be used on a target (commander or creature)
export function canUseAbilities(game: GameState, targetId: string, targetType: 'creature' | 'player' = 'player'): boolean {
  const effects = getOngoingEffectsForTarget(game, targetId, targetType);
  return !effects.some(e => e.type === OngoingEffectType.CANT_USE_ABILITIES);
}

// Get the current controller of a creature (accounting for CONTROL_SWAP effects)
export function getCreatureController(game: GameState, creatureId: string): number | null {
  const creature = findCreatureOnBattlefield(game, creatureId);
  if (!creature) return null;

  const effects = getOngoingEffectsForTarget(game, creatureId, 'creature');
  const swapEffect = effects.find(e => e.type === OngoingEffectType.CONTROL_SWAP);

  if (swapEffect && swapEffect.newControllerId !== undefined) {
    return swapEffect.newControllerId;
  }

  return creature.ownerId;
}

// Check if a specific card can be played from graveyard
export function canPlayFromGraveyard(game: GameState, playerId: number, cardId: string): boolean {
  ensureOngoingEffects(game);

  for (const effect of game.ongoingEffects!) {
    if (effect.type === OngoingEffectType.PLAY_FROM_GRAVEYARD &&
        (effect.targetType === 'player' && effect.targetId === playerId.toString() ||
         effect.targetType === 'all_players')) {
      if (!effect.allowedCardId || effect.allowedCardId === cardId) {
        return true;
      }
    }
  }

  return false;
}

// Get the total power/health modifiers for a creature
export function getCreatureModifiers(game: GameState, creatureId: string): { power: number; health: number } {
  const effects = getOngoingEffectsForTarget(game, creatureId, 'creature');

  let power = 0;
  let health = 0;

  for (const effect of effects) {
    if (effect.type === OngoingEffectType.POWER_HEALTH_BUFF) {
      power += effect.powerModifier || 0;
      health += effect.healthModifier || 0;
    }
  }

  return { power, health };
}

// Get all granted keywords for a creature
export function getGrantedKeywords(game: GameState, creatureId: string): AbilityKeyword[] {
  const effects = getOngoingEffectsForTarget(game, creatureId, 'creature');

  const keywords: AbilityKeyword[] = [];
  for (const effect of effects) {
    if (effect.type === OngoingEffectType.KEYWORD_GRANT && effect.keyword) {
      keywords.push(effect.keyword);
    }
  }

  return keywords;
}

// Get cost modifier for a card
export function getCardCostModifier(
  game: GameState,
  card: Card,
  playerId: number
): number {
  ensureOngoingEffects(game);

  let modifier = 0;

  for (const effect of game.ongoingEffects!) {
    if (effect.type === OngoingEffectType.COST_MODIFIER) {
      // Check if effect applies globally or to this player
      if (effect.targetType === 'global' ||
          (effect.targetType === 'player' && effect.targetId === playerId.toString()) ||
          effect.targetType === 'all_players') {

        // Check element type filter
        if (effect.filterElementType) {
          const cardElement = (card as CreatureCard).elementType;
          const matches = cardElement === effect.filterElementType;

          // If invertElementFilter is true, we want to apply to all EXCEPT this element
          if (effect.invertElementFilter && matches) {
            continue; // Skip if matches and we want to exclude
          } else if (!effect.invertElementFilter && !matches) {
            continue; // Skip if doesn't match and we want to include only this element
          }
        }

        // Check card type filter
        if (effect.filterCardType && card.type !== effect.filterCardType) {
          continue;
        }

        modifier += effect.costModifier || 0;
      }
    }
  }

  // Check per-card cost reductions
  const player = game.players[playerId];
  if (player.cardCostReductions && player.cardCostReductions[card.id]) {
    modifier -= player.cardCostReductions[card.id];
  }

  return modifier;
}

// Add a cost reduction for a specific card in a player's hand
export function addCardCostReduction(
  game: GameState,
  playerId: number,
  cardId: string,
  reduction: number
): void {
  const player = game.players[playerId];
  if (!player.cardCostReductions) {
    player.cardCostReductions = {};
  }
  player.cardCostReductions[cardId] = (player.cardCostReductions[cardId] || 0) + reduction;
}

// Clear cost reduction for a card (call when played)
export function clearCardCostReduction(
  game: GameState,
  playerId: number,
  cardId: string
): void {
  const player = game.players[playerId];
  if (player.cardCostReductions && player.cardCostReductions[cardId]) {
    delete player.cardCostReductions[cardId];
  }
}

// Get effective cost for a card after applying reductions
export function getEffectiveCardCost(
  game: GameState,
  playerId: number,
  cardId: string,
  baseCost: ManaPool
): ManaPool {
  const player = game.players[playerId];
  const reduction = (player.cardCostReductions && player.cardCostReductions[cardId]) || 0;

  if (reduction === 0) return baseCost;

  // Apply reduction to the first available element type
  const effectiveCost: ManaPool = { ...baseCost };
  let remainingReduction = reduction;

  for (const [element, amount] of Object.entries(effectiveCost)) {
    if (remainingReduction === 0) break;
    const elementType = element as ElementType;
    const currentAmount = amount || 0;
    const reductionToApply = Math.min(currentAmount, remainingReduction);
    effectiveCost[elementType] = Math.max(0, currentAmount - reductionToApply);
    remainingReduction -= reductionToApply;
  }

  return effectiveCost;
}

// Apply temporary transformation (stores original stats for restoration)
export function applyTemporaryTransformation(
  game: GameState,
  targetCreatureId: string,
  sourceCreatureId: string,
  duration: OngoingEffectDuration,
  createdByPlayerId: number
): boolean {
  const targetCreature = findCreatureOnBattlefield(game, targetCreatureId);
  const sourceCreature = findCreatureOnBattlefield(game, sourceCreatureId);

  if (!targetCreature || !sourceCreature) return false;

  // Store original stats in an ongoing effect
  addOngoingEffect(game, {
    type: OngoingEffectType.STAT_TRANSFORMATION,
    duration,
    targetId: targetCreatureId,
    targetType: 'creature',
    createdByPlayerId,
    originalPower: targetCreature.power,
    originalHealth: targetCreature.health,
    originalMaxHealth: targetCreature.maxHealth,
    originalKeywords: [...targetCreature.keywords],
    originalCardId: targetCreature.cardId,
  });

  // Apply the transformation
  copyCreatureStats(game, targetCreatureId, sourceCreatureId);

  return true;
}

// Clean up effects that have expired
export function cleanupExpiredEffects(game: GameState, duration: OngoingEffectDuration): void {
  ensureOngoingEffects(game);

  // First, restore stats for any STAT_TRANSFORMATION effects that are expiring
  const expiringTransformations = game.ongoingEffects!.filter(
    effect => effect.type === OngoingEffectType.STAT_TRANSFORMATION && effect.duration === duration
  );

  for (const effect of expiringTransformations) {
    const creature = findCreatureOnBattlefield(game, effect.targetId);
    if (creature && effect.originalPower !== undefined && effect.originalHealth !== undefined) {
      // Restore original stats
      creature.power = effect.originalPower;
      creature.health = effect.originalHealth;
      creature.maxHealth = effect.originalMaxHealth || effect.originalHealth;

      // Restore keywords
      if (effect.originalKeywords) {
        creature.keywords = [...effect.originalKeywords];
      }

      // Clear copied abilities
      creature.copiedAbilities = undefined;

      logEvent(game, 'ability_activated', `${ALL_CARDS[creature.cardId]?.name || 'Creature'} returned to its original form`);
    }
  }

  // Then remove all effects with matching duration
  game.ongoingEffects = game.ongoingEffects!.filter(effect => {
    return effect.duration !== duration;
  });
}

// Clean up all temporary effects at end of turn
export function cleanupEndOfTurnEffects(game: GameState): void {
  cleanupExpiredEffects(game, OngoingEffectDuration.END_OF_TURN);
}

// Clean up all effects at end of round
export function cleanupEndOfRoundEffects(game: GameState): void {
  cleanupExpiredEffects(game, OngoingEffectDuration.END_OF_ROUND);
}

// ============================================================================
// TRIGGER SYSTEM
// ============================================================================

// Ensure triggers array exists
function ensureTriggers(game: GameState): void {
  if (!game.triggers) {
    game.triggers = [];
  }
}

// Counter for unique trigger IDs
let triggerIdCounter = 0;

// Add a trigger to the game
export function addTrigger(
  game: GameState,
  trigger: Omit<Trigger, 'id' | 'createdAtTurn' | 'createdAtPhase'>
): string {
  ensureTriggers(game);

  const triggerId = `trigger_${Date.now()}_${triggerIdCounter++}`;
  const fullTrigger: Trigger = {
    ...trigger,
    id: triggerId,
    createdAtTurn: game.currentPlayerIndex,
    createdAtPhase: game.currentPhase,
  };

  game.triggers!.push(fullTrigger);
  return triggerId;
}

// Remove a trigger by ID
export function removeTrigger(game: GameState, triggerId: string): void {
  ensureTriggers(game);
  game.triggers = game.triggers!.filter(t => t.id !== triggerId);
}

// Fire all triggers of a specific type with the given context
export function fireTriggers(game: GameState, type: TriggerType, context: TriggerContext): void {
  ensureTriggers(game);

  const matchingTriggers = game.triggers!.filter(t => t.type === type);

  for (const trigger of matchingTriggers) {
    // Check optional condition
    if (trigger.condition && !trigger.condition(game, context)) {
      continue;
    }

    // Execute trigger effect
    try {
      trigger.effect(game, context);
    } catch (error) {
      logger.error(`Error executing trigger ${trigger.id}:`, error);
    }
  }
}

// Clean up expired triggers based on duration
function cleanupExpiredTriggers(game: GameState, duration: OngoingEffectDuration): void {
  ensureTriggers(game);
  game.triggers = game.triggers!.filter(t => t.duration !== duration);
}

// Clean up all triggers at end of turn
export function cleanupEndOfTurnTriggers(game: GameState): void {
  cleanupExpiredTriggers(game, OngoingEffectDuration.END_OF_TURN);
}

// Clean up all triggers at end of round
export function cleanupEndOfRoundTriggers(game: GameState): void {
  cleanupExpiredTriggers(game, OngoingEffectDuration.END_OF_ROUND);
}

// Get effective power of a creature (base + ongoing modifiers)
export function getEffectiveCreaturePower(game: GameState, creature: BattlefieldCreature): number {
  const modifiers = getCreatureModifiers(game, creature.instanceId);
  return Math.max(0, creature.power + modifiers.power);
}

// Get effective health of a creature (base + ongoing modifiers)
export function getEffectiveCreatureHealth(game: GameState, creature: BattlefieldCreature): number {
  const modifiers = getCreatureModifiers(game, creature.instanceId);
  return Math.max(0, creature.health + modifiers.health);
}

// Get effective max health of a creature (base + ongoing modifiers)
export function getEffectiveCreatureMaxHealth(game: GameState, creature: BattlefieldCreature): number {
  const modifiers = getCreatureModifiers(game, creature.instanceId);
  return Math.max(0, creature.maxHealth + modifiers.health);
}

// Get all effective keywords for a creature (base + granted)
export function getEffectiveCreatureKeywords(game: GameState, creature: BattlefieldCreature): AbilityKeyword[] {
  const baseKeywords = [...creature.keywords];
  const grantedKeywords = getGrantedKeywords(game, creature.instanceId);

  // Combine and deduplicate
  const allKeywords = [...baseKeywords, ...grantedKeywords];
  return Array.from(new Set(allKeywords));
}

// ============================================================================
// MODAL CHOICE SYSTEM
// ============================================================================

let modalChoiceIdCounter = 0;

// Create a binary choice (Yes/No, Pay/Don't Pay)
export function createBinaryChoice(
  game: GameState,
  playerId: number,
  title: string,
  description: string,
  onYes: () => void,
  onNo: () => void,
  yesLabel = 'Yes',
  noLabel = 'No',
  sourceCardId?: string
): void {
  const choiceId = `modal_${Date.now()}_${modalChoiceIdCounter++}`;

  game.pendingModalChoice = {
    id: choiceId,
    type: ModalChoiceType.BINARY,
    title,
    description,
    playerId,
    sourceCardId,
    options: [
      { id: 'yes', label: yesLabel },
      { id: 'no', label: noLabel },
    ],
    onComplete: (selectedIds: string[]) => {
      const choice = selectedIds[0];
      if (choice === 'yes') {
        onYes();
      } else {
        onNo();
      }
      game.pendingModalChoice = undefined;
    },
    onCancel: () => {
      onNo(); // Default to "no" on cancel
      game.pendingModalChoice = undefined;
    },
  };
}

// Create a "choose one" modal (e.g., "Destroy creature OR gain life")
export function createChooseOneModal(
  game: GameState,
  playerId: number,
  title: string,
  description: string,
  options: ModalChoiceOption[],
  onComplete: (selectedId: string) => void,
  sourceCardId?: string
): void {
  const choiceId = `modal_${Date.now()}_${modalChoiceIdCounter++}`;

  game.pendingModalChoice = {
    id: choiceId,
    type: ModalChoiceType.CHOOSE_ONE,
    title,
    description,
    playerId,
    sourceCardId,
    options,
    onComplete: (selectedIds: string[]) => {
      onComplete(selectedIds[0]);
      game.pendingModalChoice = undefined;
    },
    onCancel: () => {
      // Auto-select first option on cancel
      if (options.length > 0) {
        onComplete(options[0].id);
      }
      game.pendingModalChoice = undefined;
    },
  };
}

// Create a reveal information modal (e.g., "Look at opponent's hand")
export function createRevealInfoModal(
  game: GameState,
  playerId: number,
  title: string,
  description: string,
  revealedCards: { id: string; name: string; imageUrl?: string }[],
  onClose: () => void = () => {},
  sourceCardId?: string
): void {
  const choiceId = `modal_${Date.now()}_${modalChoiceIdCounter++}`;

  game.pendingModalChoice = {
    id: choiceId,
    type: ModalChoiceType.REVEAL_INFO,
    title,
    description,
    playerId,
    sourceCardId,
    revealedCards,
    onComplete: () => {
      onClose();
      game.pendingModalChoice = undefined;
    },
  };
}

// Create a confirmation modal for important actions
export function createConfirmationModal(
  game: GameState,
  playerId: number,
  title: string,
  description: string,
  onConfirm: () => void,
  onCancel: () => void = () => {},
  sourceCardId?: string
): void {
  const choiceId = `modal_${Date.now()}_${modalChoiceIdCounter++}`;

  game.pendingModalChoice = {
    id: choiceId,
    type: ModalChoiceType.CONFIRM,
    title,
    description,
    playerId,
    sourceCardId,
    onComplete: () => {
      onConfirm();
      game.pendingModalChoice = undefined;
    },
    onCancel: () => {
      onCancel();
      game.pendingModalChoice = undefined;
    },
  };
}

// Create a "choose multiple" modal (e.g., "Choose 2 cards to discard")
export function createChooseMultipleModal(
  game: GameState,
  playerId: number,
  title: string,
  description: string,
  options: ModalChoiceOption[],
  minChoices: number,
  maxChoices: number,
  onComplete: (selectedIds: string[]) => void,
  sourceCardId?: string
): void {
  const choiceId = `modal_${Date.now()}_${modalChoiceIdCounter++}`;

  game.pendingModalChoice = {
    id: choiceId,
    type: ModalChoiceType.CHOOSE_MULTIPLE,
    title,
    description,
    playerId,
    sourceCardId,
    options,
    minChoices,
    maxChoices,
    onComplete: (selectedIds: string[]) => {
      onComplete(selectedIds);
      game.pendingModalChoice = undefined;
    },
    onCancel: () => {
      // Auto-select first N options on cancel
      const defaultChoices = options.slice(0, minChoices).map(o => o.id);
      onComplete(defaultChoices);
      game.pendingModalChoice = undefined;
    },
  };
}

// Clear pending modal choice (for timeouts or forced resolution)
export function clearModalChoice(game: GameState): void {
  if (game.pendingModalChoice?.onCancel) {
    game.pendingModalChoice.onCancel();
  }
  game.pendingModalChoice = undefined;
}

// Check if there's a pending modal choice
export function hasPendingModalChoice(game: GameState): boolean {
  return game.pendingModalChoice !== undefined;
}

// ============================================================================
// GAME INITIALIZATION
// ============================================================================

export function createInitialGameState(
  player1Deck: DeckDefinition,
  player2Deck: DeckDefinition,
  environmentDeckCards: Card[],
  player1Name = 'Player 1',
  player2Name = 'Player 2'
): GameState {
  const players: PlayerState[] = [
    createPlayerState(0, player1Name, player1Deck),
    createPlayerState(1, player2Name, player2Deck),
  ];

  // Shuffle environment deck
  const environmentDeck = shuffleArray(environmentDeckCards.map((c) => c.id));

  // Randomly determine first player
  const firstPlayerIndex = Math.random() < 0.5 ? 0 : 1;

  const gameState: GameState = {
    players,
    currentPlayerIndex: firstPlayerIndex,
    firstPlayerIndex,
    currentRound: 1,
    currentPhase: Phase.ENVIRONMENT,
    environmentDeck,
    activeEnvironmentCard: null,
    cardInstances: { ...ALL_CARDS, ...ALL_ENVIRONMENT_CARDS },
    turnOrder: [0, 1],
    gameOver: false,
    winner: null,
  };

  // Draw starting hands
  for (const player of players) {
    drawCards(gameState, player.id, 7);
  }

  // Mulligan rule: if no BASIC (free) mana sources in starting hand, shuffle and redraw once
  for (const player of players) {
    const hasBasicManaSource = player.hand.some(cardId => {
      const card = gameState.cardInstances[cardId];
      if (card?.type !== CardType.MANA_SOURCE) return false;
      const manaCard = card as ManaSourceCard;
      // Basic mana sources have no cost or empty cost
      return !manaCard.cost || Object.keys(manaCard.cost).length === 0;
    });

    if (!hasBasicManaSource) {
      logEvent(gameState, 'ability_activated', `🔄 ${player.name} has no basic mana sources - taking a mulligan`);

      // Shuffle hand back into deck
      player.deck.push(...player.hand);
      player.hand = [];
      player.deck = shuffleArray(player.deck);

      // Draw new hand
      drawCards(gameState, player.id, 7);

      logEvent(gameState, 'ability_activated', `${player.name} drew a new starting hand`);
    }
  }

  return gameState;
}

function createPlayerState(
  id: number,
  name: string,
  deckDef: DeckDefinition
): PlayerState {
  // Build deck from definition
  const deck: string[] = [];
  for (const cardEntry of deckDef.cards) {
    for (let i = 0; i < cardEntry.count; i++) {
      deck.push(cardEntry.cardId);
    }
  }

  // Shuffle deck
  const shuffledDeck = shuffleArray(deck);

  // Get commander card
  const commanderCard = ALL_CARDS[deckDef.commanderId];
  if (!commanderCard || commanderCard.type !== CardType.COMMANDER) {
    throw new Error(`Invalid commander: ${deckDef.commanderId}`);
  }

  const commander: CommanderState = {
    cardId: deckDef.commanderId,
    currentHealth: commanderCard.health,
    maxHealth: commanderCard.health,
    tapped: false,
    abilityUsesThisTurn: {},
  };

  return {
    id,
    name,
    deck: shuffledDeck,
    hand: [],
    discard: [],
    exiled: [],
    commander,
    battlefield: {
      creatures: [],
      manaSources: [],
    },
    manaPool: {},
    hasPriority: false,
    hasPlayedManaThisTurn: false,
  };
}

// ============================================================================
// CARD OPERATIONS
// ============================================================================

export function drawCards(
  game: GameState,
  playerId: number,
  count: number
): void {
  // Validate inputs
  if (count <= 0) return;
  if (playerId < 0 || playerId >= game.players.length) {
    logger.error(`Invalid player ID: ${playerId}`);
    return;
  }

  const player = game.players[playerId];
  for (let i = 0; i < count; i++) {
    // If deck is empty, reshuffle discard pile into deck
    if (player.deck.length === 0) {
      if (player.discard.length > 0) {
        // Shuffle discard pile back into deck
        player.deck = [...player.discard];
        player.discard = [];
        // Shuffle the deck (Fisher-Yates algorithm)
        for (let j = player.deck.length - 1; j > 0; j--) {
          const k = Math.floor(Math.random() * (j + 1));
          [player.deck[j], player.deck[k]] = [player.deck[k], player.deck[j]];
        }
        logEvent(game, 'ability_activated', `${player.name} reshuffled discard pile into deck`);
      } else {
        // Both deck and discard are empty - can't draw
        logEvent(game, 'ability_activated', `${player.name} attempted to draw but deck and discard are empty`);
        break;
      }
    }
    const cardId = player.deck.shift()!;
    player.hand.push(cardId);
  }
}

// ============================================================================
// EFFECT HELPERS
// ============================================================================

export function dealDamage(
  game: GameState,
  targetId: string,
  targetType: 'creature' | 'player',
  damage: number,
  sourceId?: string // Optional: creature instance ID that dealt the damage
): void {
  // Validate damage amount
  if (damage < 0) {
    logger.warn(`Negative damage attempted: ${damage}`);
    return;
  }
  if (damage === 0) return;

  // Initialize damage tracking if needed
  if (!game.damageEventsThisTurn) {
    game.damageEventsThisTurn = [];
  }

  // Track damage event
  if (sourceId && damage > 0) {
    game.damageEventsThisTurn.push({
      sourceId,
      targetId,
      targetType,
      amount: damage,
      timestamp: Date.now(),
    });
  }

  if (targetType === 'player') {
    const playerId = parseInt(targetId.replace('player_', ''));
    if (isNaN(playerId) || playerId < 0 || playerId >= game.players.length) {
      logger.error(`Invalid player target: ${targetId}`);
      return;
    }
    dealDamageToCommander(game, playerId, damage);
  } else {
    const creature = findCreature(game, targetId);
    if (!creature) {
      logger.warn(`Creature not found: ${targetId}`);
      return;
    }
    dealDamageToCreature(game, creature, damage);
  }
}

export function healCommander(
  game: GameState,
  playerId: number,
  amount: number
): void {
  // Validate inputs
  if (amount <= 0) return;
  if (playerId < 0 || playerId >= game.players.length) {
    logger.error(`Invalid player ID for healing: ${playerId}`);
    return;
  }

  const player = game.players[playerId];
  player.commander.currentHealth = Math.min(
    player.commander.currentHealth + amount,
    player.commander.maxHealth
  );
}

export function healCreature(
  game: GameState,
  creatureId: string,
  amount: number
): void {
  // Validate inputs
  if (amount <= 0) return;

  const creature = findCreature(game, creatureId);
  if (!creature) {
    logger.warn(`Cannot heal creature - not found: ${creatureId}`);
    return;
  }
  creature.health = Math.min(creature.health + amount, creature.maxHealth);
}

// Add a damage prevention shield
export function addPreventionShield(
  game: GameState,
  targetId: string,
  targetType: 'player' | 'creature',
  amount: number
): void {
  if (!game.preventionShields) {
    game.preventionShields = [];
  }
  game.preventionShields.push({ targetId, targetType, amount });

  // Log the shield addition
  let targetName = 'target';
  if (targetType === 'player') {
    const playerId = parseInt(targetId.replace('player_', ''));
    targetName = game.players[playerId]?.name || `Player ${playerId + 1}`;
  } else {
    // Find creature name
    for (const player of game.players) {
      const creature = player.battlefield.creatures.find(c => c.instanceId === targetId);
      if (creature) {
        const card = game.cardInstances[creature.cardId];
        targetName = card?.name || 'creature';
        break;
      }
    }
  }
  logEvent(game, 'shield_added', `${amount} damage prevention shield added to ${targetName}`);
}

// Apply shields to reduce damage (returns actual damage after shields)
function applyPreventionShields(
  game: GameState,
  targetId: string,
  targetType: 'player' | 'creature',
  damage: number
): number {
  if (!game.preventionShields || game.preventionShields.length === 0) {
    return damage;
  }

  let remainingDamage = damage;

  // Process shields for this target
  game.preventionShields = game.preventionShields.filter(shield => {
    if (shield.targetId === targetId && shield.targetType === targetType) {
      const prevented = Math.min(shield.amount, remainingDamage);
      remainingDamage -= prevented;
      shield.amount -= prevented;

      // Keep shield if it still has capacity
      return shield.amount > 0;
    }
    return true; // Keep shields for other targets
  });

  return remainingDamage;
}

export function modifyCreaturePower(
  game: GameState,
  creatureId: string,
  powerMod: number,
  healthMod: number,
  duration: 'permanent' | 'end_of_turn' = 'end_of_turn'
): void {
  const creature = findCreature(game, creatureId);
  if (!creature) return;

  if (duration === 'permanent') {
    // Permanent modifications apply directly to base stats
    creature.power += powerMod;
    creature.health += healthMod;
    creature.maxHealth += healthMod;
  } else {
    // Temporary modifications use the ongoing effects system
    addOngoingEffect(game, {
      type: OngoingEffectType.POWER_HEALTH_BUFF,
      duration: OngoingEffectDuration.END_OF_TURN,
      targetId: creatureId,
      targetType: 'creature',
      createdByPlayerId: creature.ownerId,
      powerModifier: powerMod,
      healthModifier: healthMod,
    });
  }
}

export function createToken(
  game: GameState,
  playerId: number,
  tokenType: string,
  power: number,
  health: number
): void {
  const player = game.players[playerId];

  const token: BattlefieldCreature = {
    instanceId: generateInstanceId(),
    cardId: `token_${tokenType}`,
    ownerId: playerId,
    power,
    health,
    maxHealth: health,
    currentHealth: health,
    isTapped: false,
    summoningSick: true,
    keywords: [],
    counters: {
      plusOnePlusOne: 0,
      minusOneMinusOne: 0,
    },
    activeAbilities: [],
    isToken: true,
  } as any;

  player.battlefield.creatures.push(token);
}

export function returnCreatureToHand(
  game: GameState,
  creatureId: string
): void {
  for (const player of game.players) {
    const index = player.battlefield.creatures.findIndex(
      (c) => c.instanceId === creatureId
    );
    if (index !== -1) {
      const creature = player.battlefield.creatures[index];
      player.battlefield.creatures.splice(index, 1);
      player.hand.push(creature.cardId);
      break;
    }
  }
}

export function destroyCreature(
  game: GameState,
  creatureId: string
): void {
  const creature = findCreature(game, creatureId);
  if (creature) {
    creature.health = 0;
  }
  removeDefeatedCreatures(game);
}

export function discardCard(
  game: GameState,
  playerId: number,
  cardId: string
): void {
  const player = game.players[playerId];
  const index = player.hand.indexOf(cardId);
  if (index !== -1) {
    player.hand.splice(index, 1);
    player.discard.push(cardId);
  }
}

export function discardRandomCard(
  game: GameState,
  playerId: number
): string | null {
  const player = game.players[playerId];
  if (player.hand.length === 0) return null;

  const randomIndex = Math.floor(Math.random() * player.hand.length);
  const cardId = player.hand[randomIndex];
  player.hand.splice(randomIndex, 1);
  player.discard.push(cardId);
  return cardId;
}

export function sacrificeCreature(
  game: GameState,
  creatureId: string
): void {
  for (const player of game.players) {
    const index = player.battlefield.creatures.findIndex(
      (c) => c.instanceId === creatureId
    );
    if (index !== -1) {
      const creature = player.battlefield.creatures[index];
      player.battlefield.creatures.splice(index, 1);

      // Trigger defeat abilities
      triggerCreatureAbility(game, creatureId, 'defeat');

      // Move to discard (tokens disappear)
      if (!creature.cardId.startsWith('token_')) {
        player.discard.push(creature.cardId);
      }
      break;
    }
  }
}

export function exileCard(
  game: GameState,
  playerId: number,
  cardId: string,
  fromZone: 'hand' | 'discard' | 'deck'
): void {
  const player = game.players[playerId];
  let sourceZone: string[];

  switch (fromZone) {
    case 'hand':
      sourceZone = player.hand;
      break;
    case 'discard':
      sourceZone = player.discard;
      break;
    case 'deck':
      sourceZone = player.deck;
      break;
  }

  const index = sourceZone.indexOf(cardId);
  if (index !== -1) {
    sourceZone.splice(index, 1);
    player.exiled.push(cardId);
  }
}

export function getOpponentId(playerId: number): number {
  return playerId === 0 ? 1 : 0;
}

export function getAllCreatures(game: GameState): BattlefieldCreature[] {
  return game.players.flatMap((p) => p.battlefield.creatures);
}

export function returnCardFromGraveyard(
  game: GameState,
  playerId: number,
  cardId: string
): boolean {
  const player = game.players[playerId];
  const index = player.discard.indexOf(cardId);
  if (index === -1) return false;

  player.discard.splice(index, 1);
  player.hand.push(cardId);
  return true;
}

export function canAffordCost(manaPool: ManaPool, cost: ManaPool): boolean {
  for (const [type, amount] of Object.entries(cost)) {
    const elementType = type as ElementType;
    if ((manaPool[elementType] || 0) < (amount || 0)) {
      return false;
    }
  }
  return true;
}

export function payCost(manaPool: ManaPool, cost: ManaPool): void {
  for (const [type, amount] of Object.entries(cost)) {
    const elementType = type as ElementType;
    manaPool[elementType] = (manaPool[elementType] || 0) - (amount || 0);
  }
}

export function addMana(manaPool: ManaPool, mana: ManaPool): void {
  for (const [type, amount] of Object.entries(mana)) {
    const elementType = type as ElementType;
    manaPool[elementType] = (manaPool[elementType] || 0) + (amount || 0);
  }
}

// ============================================================================
// PLAYING CARDS
// ============================================================================

// ============================================================================
// TRIGGERED ABILITIES SYSTEM
// ============================================================================

export function triggerCreatureAbility(
  game: GameState,
  creatureInstanceId: string,
  triggerType: 'enter' | 'defeat' | 'damage' | 'attack' | 'block' | 'turn_start' | 'turn_end'
): void {
  // Find the creature
  const allCreatures = game.players.flatMap((p) => p.battlefield.creatures);
  const creature = allCreatures.find((c) => c.instanceId === creatureInstanceId);

  if (!creature) return;

  // Get the card definition
  const card = game.cardInstances[creature.cardId] as CreatureCard;
  if (!card || card.type !== CardType.CREATURE) return;

  // Check for triggered abilities
  if (!card.abilities) return;

  // Execute all abilities that match the trigger
  for (const ability of card.abilities) {
    if (ability.trigger === triggerType) {
      try {
        ability.effect(game, creatureInstanceId);
      } catch (error) {
        logger.error(`Error executing triggered ability ${ability.id}:`, error);
      }
    }
  }
}

export function triggerAllCreaturesOnEvent(
  game: GameState,
  triggerType: 'turn_start' | 'turn_end',
  playerId?: number
): void {
  const creatures = playerId !== undefined
    ? game.players[playerId].battlefield.creatures
    : game.players.flatMap((p) => p.battlefield.creatures);

  for (const creature of creatures) {
    triggerCreatureAbility(game, creature.instanceId, triggerType);
  }
}

// ============================================================================
// CARD PLAYING FUNCTIONS
// ============================================================================

export function playCreature(
  game: GameState,
  playerId: number,
  cardId: string
): boolean {
  const player = game.players[playerId];
  const card = game.cardInstances[cardId];

  if (!card || card.type !== CardType.CREATURE) return false;
  const creatureCard = card as CreatureCard;

  // Check if in hand
  if (!player.hand.includes(cardId)) return false;

  // Get effective cost with reductions applied
  const effectiveCost = getEffectiveCardCost(game, playerId, cardId, creatureCard.cost);

  // Check mana cost
  if (!canAffordCost(player.manaPool, effectiveCost)) return false;

  // Pay cost
  payCost(player.manaPool, effectiveCost);

  // Clear any cost reduction for this card
  clearCardCostReduction(game, playerId, cardId);

  // Remove from hand (only remove ONE instance, not all)
  const cardIndex = player.hand.indexOf(cardId);
  if (cardIndex !== -1) {
    player.hand.splice(cardIndex, 1);
  }

  // Create battlefield creature
  const battlefieldCreature: BattlefieldCreature = {
    instanceId: generateInstanceId(),
    cardId,
    ownerId: playerId,
    power: creatureCard.power,
    health: creatureCard.health,
    maxHealth: creatureCard.health,
    currentHealth: creatureCard.health,
    isTapped: false,
    summoningSick: true,
    keywords: [...creatureCard.keywords],
    counters: {
      plusOnePlusOne: 0,
      minusOneMinusOne: 0,
    },
    activeAbilities: [],
  };

  player.battlefield.creatures.push(battlefieldCreature);

  // Log creature summon
  logEvent(game, 'ability_activated', `🎴 ${player.name} summoned ${creatureCard.name} (${creatureCard.power}/${creatureCard.health})`);

  // Trigger enter battlefield effects
  triggerCreatureAbility(game, battlefieldCreature.instanceId, 'enter');

  // Fire ON_CREATURE_ENTER triggers
  fireTriggers(game, TriggerType.ON_CREATURE_ENTER, {
    creatureId: battlefieldCreature.instanceId,
    playerId: playerId,
  });

  return true;
}

export function playManaSource(
  game: GameState,
  playerId: number,
  cardId: string
): boolean {
  const player = game.players[playerId];
  const card = game.cardInstances[cardId];

  if (!card || card.type !== CardType.MANA_SOURCE) return false;
  const manaSourceCard = card as ManaSourceCard;

  // Check if in hand
  if (!player.hand.includes(cardId)) return false;

  // Check if already played a mana source this turn (rule: 1 per turn typically)
  if (player.hasPlayedManaThisTurn) return false;

  // Check and pay cost for advanced mana sources
  if (manaSourceCard.cost && Object.keys(manaSourceCard.cost).length > 0) {
    if (!canAffordCost(player.manaPool, manaSourceCard.cost)) {
      return false;
    }
    payCost(player.manaPool, manaSourceCard.cost);
  }

  // Remove from hand (only remove ONE instance, not all)
  const cardIndex = player.hand.indexOf(cardId);
  if (cardIndex !== -1) {
    player.hand.splice(cardIndex, 1);
  }

  // Create battlefield mana source (starts tapped - summoning sickness)
  const battlefieldManaSource: BattlefieldManaSource = {
    instanceId: generateInstanceId(),
    cardId,
    ownerId: playerId,
    isTapped: true, // Mana sources start tapped (can't use same turn placed)
  };

  player.battlefield.manaSources.push(battlefieldManaSource);
  player.hasPlayedManaThisTurn = true;

  // Log mana source played
  logEvent(game, 'ability_activated', `💎 ${player.name} played mana source: ${manaSourceCard.name}`);

  return true;
}

export function playArt(
  game: GameState,
  playerId: number,
  cardId: string,
  targets?: any
): boolean {
  const player = game.players[playerId];
  const card = game.cardInstances[cardId];

  if (!card || card.type !== CardType.ART) return false;
  const artCard = card as ArtCard;

  // Check if in hand
  if (!player.hand.includes(cardId)) return false;

  // Get effective cost with reductions applied
  const effectiveCost = getEffectiveCardCost(game, playerId, cardId, artCard.cost);

  // Check mana cost
  if (!canAffordCost(player.manaPool, effectiveCost)) return false;

  // Pay cost
  payCost(player.manaPool, effectiveCost);

  // Clear any cost reduction for this card
  clearCardCostReduction(game, playerId, cardId);

  // Remove from hand and discard (only remove ONE instance, not all)
  const cardIndex = player.hand.indexOf(cardId);
  if (cardIndex !== -1) {
    player.hand.splice(cardIndex, 1);
  }
  player.discard.push(cardId);

  // Log art cast
  logEvent(game, 'ability_activated', `✨ ${player.name} cast art: ${artCard.name}`);

  // Execute art effect
  if (artCard.effect) {
    artCard.effect(game, playerId, targets);
  }

  return true;
}

// ============================================================================
// MANA OPERATIONS
// ============================================================================

export function tapManaSource(
  game: GameState,
  playerId: number,
  instanceId: string
): boolean {
  const player = game.players[playerId];
  const manaSource = player.battlefield.manaSources.find((m) => m.instanceId === instanceId);

  if (!manaSource || manaSource.isTapped) return false;

  const card = game.cardInstances[manaSource.cardId];
  if (!card || card.type !== CardType.MANA_SOURCE) return false;

  const manaSourceCard = card as ManaSourceCard;

  // Tap and add mana
  manaSource.isTapped = true;
  addMana(player.manaPool, manaSourceCard.manaGenerated);

  // Trigger tap abilities (e.g., Raging Caldera damage, Healing Garden heal)
  if (manaSourceCard.abilities) {
    for (const ability of manaSourceCard.abilities) {
      if (ability.effect) {
        try {
          ability.effect(game, playerId);
        } catch (error) {
          logger.error(`Error executing mana source ability ${ability.id}:`, error);
        }
      }
    }
  }

  return true;
}

export function untapAll(game: GameState, playerId: number): void {
  const player = game.players[playerId];

  // Untap all creatures
  for (const creature of player.battlefield.creatures) {
    creature.isTapped = false;
    creature.summoningSick = false;
  }

  // Untap all mana sources
  for (const manaSource of player.battlefield.manaSources) {
    manaSource.isTapped = false;
  }

  // Untap commander
  if (player.commander.tapped) {
    player.commander.tapped = false;
    const commanderCard = game.cardInstances[player.commander.cardId];
    if (commanderCard) {
      logEvent(game, 'ability_activated', `${commanderCard.name} untapped`);
    }
  }
}

export function emptyManaPool(player: PlayerState): void {
  player.manaPool = {};
}

// ============================================================================
// COMBAT SYSTEM
// ============================================================================

export function declareAttackers(
  game: GameState,
  playerId: number,
  attacks: CombatDeclaration[]
): boolean {
  const player = game.players[playerId];

  // Validate all attackers
  for (const attack of attacks) {
    const creature = player.battlefield.creatures.find(
      (c) => c.instanceId === attack.attackerId
    );

    if (!creature) return false;
    if (creature.isTapped) return false;
    if (creature.summoningSick) return false;

    // Tap attacking creature
    creature.isTapped = true;

    // Trigger attack abilities
    triggerCreatureAbility(game, creature.instanceId, 'attack');
  }

  return true;
}

export function declareBlockers(
  game: GameState,
  playerId: number,
  blocks: BlockDeclaration[]
): boolean {
  const player = game.players[playerId];

  // Initialize block assignments for block triggers
  game.blockAssignments = {};

  // Validate all blockers
  for (const block of blocks) {
    const blocker = player.battlefield.creatures.find(
      (c) => c.instanceId === block.blockerId
    );

    if (!blocker) return false;
    if (blocker.isTapped) return false;

    // Tap blocking creature
    blocker.isTapped = true;

    // Store block assignment for block triggers
    game.blockAssignments[block.blockerId] = block.attackerId;

    // Log block trigger
    const blockerCard = game.cardInstances[blocker.cardId];
    if (blockerCard) {
      logEvent(game, 'block_trigger', `${blockerCard.name} blocks`);
    }

    // Trigger block abilities
    triggerCreatureAbility(game, blocker.instanceId, 'block');
  }

  // Clear block assignments after triggers resolve
  game.blockAssignments = undefined;

  return true;
}

export function resolveCombat(
  game: GameState,
  attacks: CombatDeclaration[],
  blocks: BlockDeclaration[]
): void {
  // Initialize attack target tracking
  if (!game.attackTargets) {
    game.attackTargets = {};
  }

  // Track what each attacker is targeting
  for (const attack of attacks) {
    game.attackTargets[attack.attackerId] = {
      targetId: attack.targetId,
      targetType: attack.targetType,
    };
  }

  // Create a map of attackers to blockers
  const attackerToBlockers = new Map<string, string[]>();
  for (const block of blocks) {
    if (!attackerToBlockers.has(block.attackerId)) {
      attackerToBlockers.set(block.attackerId, []);
    }
    attackerToBlockers.get(block.attackerId)!.push(block.blockerId);
  }

  // Fire ON_CREATURE_ATTACK triggers for all attackers
  for (const attack of attacks) {
    const attacker = findCreature(game, attack.attackerId);
    if (attacker) {
      fireTriggers(game, TriggerType.ON_CREATURE_ATTACK, {
        creatureId: attack.attackerId,
        playerId: attacker.ownerId,
      });
    }
  }

  // Resolve each attack
  for (const attack of attacks) {
    const attacker = findCreature(game, attack.attackerId);
    if (!attacker) continue;

    const blockers = attackerToBlockers.get(attack.attackerId) || [];

    if (blockers.length === 0) {
      // Unblocked - damage goes to target
      const attackerPower = getEffectiveCreaturePower(game, attacker);

      if (attack.targetType === 'player') {
        // Use dealDamage with sourceId for tracking
        dealDamage(game, attack.targetId, 'player', attackerPower, attack.attackerId);
      } else {
        // Creature to creature (unblocked)
        dealDamage(game, attack.targetId, 'creature', attackerPower, attack.attackerId);
      }
    } else {
      // Blocked - resolve combat between attacker and blockers
      for (const blockerId of blockers) {
        const blocker = findCreature(game, blockerId);
        if (!blocker) continue;

        // Check type advantage
        const attackerCard = game.cardInstances[attacker.cardId] as CreatureCard;
        const blockerCard = game.cardInstances[blocker.cardId] as CreatureCard;

        // Get effective power with ongoing modifiers
        let attackerPower = getEffectiveCreaturePower(game, attacker);
        let blockerPower = getEffectiveCreaturePower(game, blocker);

        // Apply type advantage (+1 power)
        if (hasTypeAdvantage(attackerCard.elementType, blockerCard.elementType)) {
          attackerPower += 1;
        }
        if (hasTypeAdvantage(blockerCard.elementType, attackerCard.elementType)) {
          blockerPower += 1;
        }

        // Deal damage simultaneously with sourceId tracking
        dealDamage(game, attacker.instanceId, 'creature', blockerPower, blockerId);
        dealDamage(game, blocker.instanceId, 'creature', attackerPower, attacker.instanceId);
      }
    }
  }

  // Remove defeated creatures
  removeDefeatedCreatures(game);
}

function findCreature(game: GameState, instanceId: string): BattlefieldCreature | null {
  for (const player of game.players) {
    const creature = player.battlefield.creatures.find((c) => c.instanceId === instanceId);
    if (creature) return creature;
  }
  return null;
}

function dealDamageToCreature(
  game: GameState,
  creature: BattlefieldCreature,
  damage: number
): void {
  // Apply prevention shields
  const actualDamage = applyPreventionShields(game, creature.instanceId, 'creature', damage);

  creature.health -= actualDamage;

  // Trigger damage received abilities (only if damage was actually dealt)
  if (actualDamage > 0) {
    triggerCreatureAbility(game, creature.instanceId, 'damage');
  }
}

function dealDamageToCommander(
  game: GameState,
  playerId: number,
  damage: number
): void {
  // Apply prevention shields
  const actualDamage = applyPreventionShields(game, `player_${playerId}`, 'player', damage);

  const player = game.players[playerId];
  player.commander.currentHealth -= actualDamage;

  // Check if commander is defeated
  if (player.commander.currentHealth <= 0) {
    player.commander.currentHealth = 0;
    // Game over for this player
    checkGameOver(game);
  }
}

function removeDefeatedCreatures(game: GameState): void {
  // Initialize defeat tracking if needed
  if (!game.creaturesDefeatedThisTurn) {
    game.creaturesDefeatedThisTurn = [];
  }

  for (const player of game.players) {
    const defeated: BattlefieldCreature[] = [];

    player.battlefield.creatures = player.battlefield.creatures.filter((creature) => {
      // Check effective health (includes ongoing modifiers)
      const effectiveHealth = getEffectiveCreatureHealth(game, creature);
      if (effectiveHealth <= 0) {
        defeated.push(creature);
        return false;
      }
      return true;
    });

    // Move defeated creatures to discard and trigger defeat effects
    for (const creature of defeated) {
      // Track the defeat
      game.creaturesDefeatedThisTurn.push({ instanceId: creature.instanceId });

      // Log the defeat
      const card = game.cardInstances[creature.cardId];
      const creatureName = card?.name || 'Creature';
      logEvent(game, 'creature_defeated', `${creatureName} was defeated`);

      // Trigger defeat abilities BEFORE moving to discard
      triggerCreatureAbility(game, creature.instanceId, 'defeat');

      // Move to discard
      player.discard.push(creature.cardId);
    }
  }
}

// Immediate single-attack combat resolution (simplified system)
export function resolveSingleAttack(
  game: GameState,
  attackerCreatureId: string,
  targetType: 'creature' | 'player',
  targetId: string
): boolean {
  const attacker = findCreature(game, attackerCreatureId);
  if (!attacker) return false;

  // Check if attacker can attack
  if (attacker.isTapped || attacker.summoningSick) {
    return false;
  }

  // Tap the attacker
  attacker.isTapped = true;

  // Fire attack triggers
  fireTriggers(game, TriggerType.ON_CREATURE_ATTACK, {
    creatureId: attackerCreatureId,
    playerId: attacker.ownerId,
  });

  const attackerPower = getEffectiveCreaturePower(game, attacker);
  const attackerCard = game.cardInstances[attacker.cardId] as CreatureCard;

  if (targetType === 'player') {
    // Attack opponent's commander
    const targetPlayerId = parseInt(targetId);
    const targetPlayer = game.players[targetPlayerId];

    logEvent(
      game,
      'ability_activated',
      `⚔️ ${attackerCard?.name || 'Attacker'} (${attackerPower}/${attacker.health}) attacks ${targetPlayer.name}'s commander!`
    );

    // Deal damage to commander (format targetId as "player_X")
    dealDamage(game, `player_${targetPlayerId}`, 'player', attackerPower, attackerCreatureId);

  } else {
    // Attack opponent's creature
    const targetCreature = findCreature(game, targetId);
    if (!targetCreature) return false;

    const targetCard = game.cardInstances[targetCreature.cardId] as CreatureCard;
    const targetPower = getEffectiveCreaturePower(game, targetCreature);

    logEvent(
      game,
      'ability_activated',
      `⚔️ ${attackerCard?.name || 'Attacker'} (${attackerPower}/${attacker.health}) attacks ${targetCard?.name || 'Defender'} (${targetPower}/${targetCreature.health})!`
    );

    // Both creatures deal damage to each other
    const hasFirstStrike = attacker.keywords.includes(AbilityKeyword.FIRST_STRIKE) ||
                          attacker.keywords.includes(AbilityKeyword.DOUBLE_STRIKE);
    const targetHasFirstStrike = targetCreature.keywords.includes(AbilityKeyword.FIRST_STRIKE) ||
                                targetCreature.keywords.includes(AbilityKeyword.DOUBLE_STRIKE);

    // First strike damage
    if (hasFirstStrike && !targetHasFirstStrike) {
      targetCreature.health -= attackerPower;
      if (targetCreature.health <= 0) {
        logEvent(game, 'ability_activated', `💀 ${targetCard?.name} was defeated by first strike!`);
        destroyCreature(game, targetId);
        return true; // Attacker survives
      }
    }

    // Regular combat damage
    attacker.health -= targetPower;
    targetCreature.health -= attackerPower;

    // Check for deaths
    const attackerDied = attacker.health <= 0;
    const targetDied = targetCreature.health <= 0;

    if (attackerDied && targetDied) {
      logEvent(game, 'ability_activated', `💀 Both ${attackerCard?.name} and ${targetCard?.name} were defeated!`);
      destroyCreature(game, attackerCreatureId);
      destroyCreature(game, targetId);
    } else if (attackerDied) {
      logEvent(game, 'ability_activated', `💀 ${attackerCard?.name} was defeated!`);
      destroyCreature(game, attackerCreatureId);
    } else if (targetDied) {
      logEvent(game, 'ability_activated', `💀 ${targetCard?.name} was defeated!`);
      destroyCreature(game, targetId);
    }
  }

  // Check if game is over
  checkGameOver(game);

  return true;
}

function checkGameOver(game: GameState): void {
  const alivePlayers = game.players.filter((p) => p.commander.currentHealth > 0);

  if (alivePlayers.length === 1) {
    game.gameOver = true;
    game.winner = alivePlayers[0].id;
  } else if (alivePlayers.length === 0) {
    game.gameOver = true;
    game.winner = null; // Draw
  }
}

// ============================================================================
// TURN STRUCTURE
// ============================================================================

export function advancePhase(game: GameState): void {
  // Player phases (environment is separate)
  const playerPhaseOrder = [
    Phase.OPENING,
    Phase.RISING_ACTION,
    Phase.CONFLICT,
    Phase.RESOLUTION,
    Phase.END_OF_ROUND,
  ];

  // If we're in environment phase, auto-advance to first player's opening phase
  if (game.currentPhase === Phase.ENVIRONMENT) {
    // Environment phase auto-advances
    flipEnvironmentCard(game);

    // Move to first player's opening phase
    game.currentPlayerIndex = game.firstPlayerIndex;
    game.currentPhase = Phase.OPENING;

    // Execute opening phase logic
    executePhaseLogic(game);
    return;
  }

  // Normal player phase advancement
  const currentIndex = playerPhaseOrder.indexOf(game.currentPhase);
  const nextIndex = (currentIndex + 1) % playerPhaseOrder.length;
  game.currentPhase = playerPhaseOrder[nextIndex];

  // Execute phase-specific logic
  executePhaseLogic(game);
}

function executePhaseLogic(game: GameState): void {
  const currentPlayer = game.players[game.currentPlayerIndex];

  switch (game.currentPhase) {
    case Phase.ENVIRONMENT:
      // Environment phase is handled in advancePhase and auto-advances
      break;

    case Phase.OPENING: {
      logger.log('📖 OPENING phase executing for player', currentPlayer.id, '- Round:', game.currentRound);
      const openingPlayerName = currentPlayer.name === 'You' ? 'Your' : `${currentPlayer.name}'s`;
      logEvent(game, 'ability_activated', `📖 ${openingPlayerName} OPENING phase - Untap and draw`);
      // Untap all
      logger.log('🔓 Calling untapAll for player', currentPlayer.id);
      untapAll(game, currentPlayer.id);
      // Draw back up to 7 cards
      const cardsToDraw = Math.max(0, 7 - currentPlayer.hand.length);
      if (cardsToDraw > 0) {
        drawCards(game, currentPlayer.id, cardsToDraw);
        logEvent(game, 'ability_activated', `${currentPlayer.name} drew ${cardsToDraw} card${cardsToDraw > 1 ? 's' : ''} (back up to ${currentPlayer.hand.length})`);
      } else {
        const verb = currentPlayer.name === 'You' ? 'have' : 'has';
        logEvent(game, 'ability_activated', `${currentPlayer.name} already ${verb} 7+ cards, no draw`);
      }
      // Reset mana source play flag
      currentPlayer.hasPlayedManaThisTurn = false;
      // Reset commander ability uses
      resetCommanderAbilityUses(game, currentPlayer.id);
      // Reset creature defeat tracking for this turn
      game.creaturesDefeatedThisTurn = [];
      // Reset damage event tracking
      game.damageEventsThisTurn = [];
      // Reset attack target tracking
      game.attackTargets = {};
      break;
    }

    case Phase.RISING_ACTION: {
      // Main phase - player can play cards
      const risingActionPlayerName = currentPlayer.name === 'You' ? 'Your' : `${currentPlayer.name}'s`;
      logEvent(game, 'ability_activated', `⚔️ ${risingActionPlayerName} RISING ACTION phase - Play creatures and arts`);
      break;
    }

    case Phase.CONFLICT: {
      // Combat phase
      const conflictPlayerName = currentPlayer.name === 'You' ? 'Your' : `${currentPlayer.name}'s`;
      logEvent(game, 'ability_activated', `⚔️ ${conflictPlayerName} CONFLICT phase - Declare attackers and blockers`);
      break;
    }

    case Phase.RESOLUTION: {
      // End phase
      const resolutionPlayerName = currentPlayer.name === 'You' ? 'Your' : `${currentPlayer.name}'s`;
      logEvent(game, 'ability_activated', `🔮 ${resolutionPlayerName} RESOLUTION phase - Play arts and resolve effects`);
      // Clean up end-of-turn effects
      cleanupEndOfTurnEffects(game);
      cleanupEndOfTurnTriggers(game);
      break;
    }

    case Phase.END_OF_ROUND:
      logger.log('📍 END_OF_ROUND phase - current round:', game.currentRound);
      // Move to next player (sets phase to ENVIRONMENT or OPENING)
      advanceToNextPlayer(game);
      logger.log('📍 After advanceToNextPlayer - new phase:', game.currentPhase);

      // If we moved to OPENING phase, execute it now
      if ((game.currentPhase as Phase) === Phase.OPENING) {
        const newPlayer = game.players[game.currentPlayerIndex];
        logger.log('📖 OPENING phase executing for player', newPlayer.id, '- Round:', game.currentRound);
        const openingPlayerName = newPlayer.name === 'You' ? 'Your' : `${newPlayer.name}'s`;
        logEvent(game, 'ability_activated', `📖 ${openingPlayerName} OPENING phase - Untap and draw`);
        // Untap all
        logger.log('🔓 Calling untapAll for player', newPlayer.id);
        untapAll(game, game.currentPlayerIndex);
        // Draw back up to 7 cards
        const cardsToDraw = Math.max(0, 7 - newPlayer.hand.length);
        if (cardsToDraw > 0) {
          drawCards(game, game.currentPlayerIndex, cardsToDraw);
          logEvent(game, 'ability_activated', `${newPlayer.name} drew ${cardsToDraw} card${cardsToDraw > 1 ? 's' : ''} (back up to ${newPlayer.hand.length})`);
        } else {
          const verb = newPlayer.name === 'You' ? 'have' : 'has';
          logEvent(game, 'ability_activated', `${newPlayer.name} already ${verb} 7+ cards, no draw`);
        }
        // Reset mana source play flag
        newPlayer.hasPlayedManaThisTurn = false;
        // Reset commander ability uses
        resetCommanderAbilityUses(game, newPlayer.id);
        // Reset creature defeat tracking for this turn
        game.creaturesDefeatedThisTurn = [];
        // Reset damage event tracking
        game.damageEventsThisTurn = [];
        // Reset attack target tracking
        game.attackTargets = {};
      }
      // If we moved to ENVIRONMENT, that will be handled when advancePhase is called next
      break;
  }
}

function advanceToNextPlayer(game: GameState): void {
  const currentIndex = game.turnOrder.indexOf(game.currentPlayerIndex);
  const nextIndex = (currentIndex + 1) % game.turnOrder.length;

  // Check if we've completed a full round (next player would be first player)
  const wouldBeFirstPlayer = game.turnOrder[nextIndex] === game.firstPlayerIndex;

  if (wouldBeFirstPlayer) {
    // All players have gone - round is complete, increment counter
    const oldRound = game.currentRound;
    game.currentRound++;
    logger.log(`🔢 Round incremented from ${oldRound} to ${game.currentRound} (all players completed their turns)`);

    // Trigger environment phase
    logEvent(game, 'ability_activated', '🌍 Environment Phase - The world shifts...');
    game.currentPhase = Phase.ENVIRONMENT;
    // Don't advance player index yet - environment phase will handle that
  } else {
    // Move to next player
    game.currentPlayerIndex = game.turnOrder[nextIndex];
    // Start their opening phase
    game.currentPhase = Phase.OPENING;
  }
}

function flipEnvironmentCard(game: GameState): void {
  logger.log('🎴 flipEnvironmentCard called - current round:', game.currentRound);
  // End of round - empty all mana pools
  for (const player of game.players) {
    emptyManaPool(player);
  }

  // Clean up end-of-round effects
  cleanupEndOfRoundEffects(game);
  cleanupEndOfRoundTriggers(game);

  // Rotate first player (only in 3+ player games, 2-player games have static first player)
  if (game.players.length >= 3) {
    const firstIndex = game.turnOrder.indexOf(game.firstPlayerIndex);
    game.firstPlayerIndex = game.turnOrder[(firstIndex + 1) % game.turnOrder.length];
  }

  if (game.environmentDeck.length === 0) {
    // Reshuffle environment deck
    const allEnvCards = Object.values(game.cardInstances).filter(
      (c) => c.type === CardType.ENVIRONMENT
    );
    game.environmentDeck = shuffleArray(allEnvCards.map((c) => c.id));
  }

  if (game.environmentDeck.length > 0) {
    const cardId = game.environmentDeck.shift()!;
    game.activeEnvironmentCard = cardId;

    // Execute environment card effect
    const envCard = game.cardInstances[cardId];
    if (envCard && envCard.type === CardType.ENVIRONMENT) {
      const environmentCard = envCard as EnvironmentCard;

      // Log environment card activation
      logEvent(game, 'ability_activated', `🌍 Environment: ${environmentCard.name}`);

      // Show description if available, otherwise show flavor text
      if (environmentCard.description) {
        logEvent(game, 'ability_activated', `📜 Effect: ${environmentCard.description}`);
      }
      if (environmentCard.flavorText) {
        logEvent(game, 'ability_activated', `💭 ${environmentCard.flavorText}`);
      }

      if (environmentCard.effect) {
        try {
          environmentCard.effect(game);
          // Show description if provided, otherwise show generic message
          if (environmentCard.description) {
            logEvent(game, 'ability_activated', `⚡ ${environmentCard.name}: ${environmentCard.description}`);
          } else {
            logEvent(game, 'ability_activated', `⚡ ${environmentCard.name} effects have been applied to the battlefield`);
          }
        } catch (error) {
          logger.error(`Error executing environment effect ${cardId}:`, error);
        }
      }
    }
  }

  // Log round transition
  logEvent(game, 'ability_activated', `📜 Round ${game.currentRound} begins! First player: ${game.players[game.firstPlayerIndex].name}`);
}

// ============================================================================
// COMMANDER ABILITIES
// ============================================================================

export function activateCommanderAbility(
  game: GameState,
  playerId: number,
  abilityId: string
): boolean {
  const player = game.players[playerId];
  const commanderCard = game.cardInstances[player.commander.cardId];

  if (!commanderCard || commanderCard.type !== CardType.COMMANDER) return false;

  const ability = commanderCard.abilities.find((a) => a.id === abilityId);
  if (!ability) return false;

  // Commander must not be tapped to use ANY ability
  if (player.commander.tapped) return false;

  // If commander is tapped, a tap ability was already used - can't use spell-like abilities after
  // (This is redundant with above check but kept for clarity)

  // Tap abilities can only be used once per turn (enforced by tapping)
  // Spell-like abilities must be used BEFORE tap abilities (enforced by tapped check)

  // Check uses per turn (only for non-tap abilities)
  if (ability.usesPerTurn && !ability.tapCost) {
    const uses = player.commander.abilityUsesThisTurn[abilityId] || 0;
    if (uses >= ability.usesPerTurn) return false;
  }

  // Check mana cost
  if (!canAffordCost(player.manaPool, ability.cost)) return false;

  // Pay cost
  payCost(player.manaPool, ability.cost);

  // Tap commander if ability requires it
  if (ability.tapCost) {
    tapCommander(game, playerId);
  }

  // Track usage (only for non-tap abilities)
  if (!ability.tapCost) {
    player.commander.abilityUsesThisTurn[abilityId] =
      (player.commander.abilityUsesThisTurn[abilityId] || 0) + 1;
  }

  // Execute effect
  if (ability.effect) {
    ability.effect(game, playerId);
  }

  return true;
}

export function resetCommanderAbilityUses(game: GameState, playerId: number): void {
  game.players[playerId].commander.abilityUsesThisTurn = {};
}

// ============================================================================
// TARGET SELECTION
// ============================================================================

// Request target selection from a player
export function requestTargets(
  game: GameState,
  request: {
    playerId: number;
    title: string;
    description: string;
    targetType: 'creature' | 'player' | 'any';
    minTargets?: number;
    maxTargets?: number;
    filter?: (targetId: string, game: GameState) => boolean;
    sourceCardId?: string;
    sourceAbilityId?: string;
  },
  onComplete: (selection: import('./types').TargetSelection) => void,
  onCancel?: () => void
): void {
  game.pendingTargetSelection = {
    id: generateInstanceId(),
    playerId: request.playerId,
    title: request.title,
    description: request.description,
    targetType: request.targetType,
    minTargets: request.minTargets || 1,
    maxTargets: request.maxTargets || 1,
    filter: request.filter,
    sourceCardId: request.sourceCardId,
    sourceAbilityId: request.sourceAbilityId,
    onComplete,
    onCancel,
  };
}

// Clear pending target selection
export function clearTargetSelection(game: GameState): void {
  game.pendingTargetSelection = undefined;
}

// ============================================================================
// DAMAGE EVENT TRACKING
// ============================================================================

// Check if a creature dealt damage to a specific GM this turn
export function didCreatureDamageGM(game: GameState, creatureId: string, gmPlayerId: number): boolean {
  if (!game.damageEventsThisTurn) return false;

  return game.damageEventsThisTurn.some(
    event =>
      event.sourceId === creatureId &&
      event.targetType === 'player' &&
      event.targetId === `player_${gmPlayerId}`
  );
}

// Check if a creature dealt any damage to any GM this turn
export function didCreatureDamageAnyGM(game: GameState, creatureId: string): boolean {
  if (!game.damageEventsThisTurn) return false;

  return game.damageEventsThisTurn.some(
    event => event.sourceId === creatureId && event.targetType === 'player'
  );
}

// Get all damage events for a specific creature (as source)
export function getCreatureDamageEvents(game: GameState, creatureId: string) {
  if (!game.damageEventsThisTurn) return [];
  return game.damageEventsThisTurn.filter(event => event.sourceId === creatureId);
}

// Get damage events where a creature was damaged (as target)
export function getDamageReceivedByCreature(game: GameState, creatureId: string) {
  if (!game.damageEventsThisTurn) return [];
  return game.damageEventsThisTurn.filter(
    event => event.targetType === 'creature' && event.targetId === creatureId
  );
}

// Clear damage tracking at start of turn
export function clearDamageTracking(game: GameState): void {
  game.damageEventsThisTurn = [];
}

// ============================================================================
// SIDEBOARD FUNCTIONS
// ============================================================================

// Swap a card from deck to sideboard
export function swapDeckToSideboard(
  game: GameState,
  playerId: number,
  cardId: string
): boolean {
  const player = game.players[playerId];

  // Ensure sideboard exists
  if (!player.sideboard) {
    player.sideboard = [];
  }

  // Find card in deck
  const deckIndex = player.deck.indexOf(cardId);
  if (deckIndex === -1) {
    logger.warn(`Card not found in deck: ${cardId}`);
    return false;
  }

  // Move from deck to sideboard
  player.deck.splice(deckIndex, 1);
  player.sideboard.push(cardId);
  return true;
}

// Swap a card from sideboard to deck
export function swapSideboardToDeck(
  game: GameState,
  playerId: number,
  cardId: string
): boolean {
  const player = game.players[playerId];

  // Ensure sideboard exists
  if (!player.sideboard) {
    logger.warn('Player has no sideboard');
    return false;
  }

  // Find card in sideboard
  const sideboardIndex = player.sideboard.indexOf(cardId);
  if (sideboardIndex === -1) {
    logger.warn(`Card not found in sideboard: ${cardId}`);
    return false;
  }

  // Move from sideboard to deck
  player.sideboard.splice(sideboardIndex, 1);
  player.deck.push(cardId);
  return true;
}

// Get sideboard contents with card names
export function getSideboardContents(
  game: GameState,
  playerId: number
): { id: string; name: string; count: number }[] {
  const player = game.players[playerId];
  if (!player.sideboard || player.sideboard.length === 0) {
    return [];
  }

  // Count cards
  const cardCounts: Record<string, number> = {};
  for (const cardId of player.sideboard) {
    cardCounts[cardId] = (cardCounts[cardId] || 0) + 1;
  }

  // Convert to array with names
  return Object.entries(cardCounts).map(([id, count]) => ({
    id,
    name: game.cardInstances[id]?.name || id,
    count,
  }));
}

// ============================================================================
// EXPORTS
// ============================================================================

export const GameLogic = {
  createInitialGameState,
  drawCards,
  discardCard,
  discardRandomCard,
  playCreature,
  playManaSource,
  playArt,
  tapManaSource,
  untapAll,
  emptyManaPool,
  declareAttackers,
  declareBlockers,
  resolveCombat,
  resolveSingleAttack,
  advancePhase,
  activateCommanderAbility,
  resetCommanderAbilityUses,
  canAffordCost,
  payCost,
  addMana,
  rollDie,
  rollDieTracked,
  rerollLastDie,
  flipCoin,
  dealDamage,
  healCommander,
  healCreature,
  addPreventionShield,
  modifyCreaturePower,
  createToken,
  returnCreatureToHand,
  destroyCreature,
  sacrificeCreature,
  exileCard,
  getOpponentId,
  getAllCreatures,
  returnCardFromGraveyard,
  triggerCreatureAbility,
  triggerAllCreaturesOnEvent,
  logEvent,
  sendChatMessage,
  // Ongoing effects system
  addOngoingEffect,
  removeOngoingEffect,
  getOngoingEffectsForTarget,
  canCreatureAttack,
  canCreatureBlock,
  canTap,
  canUntap,
  getCreatureModifiers,
  getGrantedKeywords,
  getCardCostModifier,
  addCardCostReduction,
  clearCardCostReduction,
  getEffectiveCardCost,
  applyTemporaryTransformation,
  cleanupEndOfTurnEffects,
  cleanupEndOfRoundEffects,
  getEffectiveCreaturePower,
  getEffectiveCreatureHealth,
  getEffectiveCreatureMaxHealth,
  getEffectiveCreatureKeywords,
  // Modal choice system
  createBinaryChoice,
  createChooseOneModal,
  createRevealInfoModal,
  createConfirmationModal,
  createChooseMultipleModal,
  clearModalChoice,
  hasPendingModalChoice,
  // Damage tracking
  didCreatureDamageGM,
  didCreatureDamageAnyGM,
  getCreatureDamageEvents,
  clearDamageTracking,
  // Sideboard functions
  swapDeckToSideboard,
  swapSideboardToDeck,
  getSideboardContents,
  // Target selection
  requestTargets,
  clearTargetSelection,
};
